import { STAGES, type Stage } from '@league/shared/applications';
import { DIRECTOR_SCREENING_IN_V1 } from '@league/shared/features';
import { LINK_CLASS } from '@league/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { apiFetch } from '../../lib/api';
import { HANDS_WON_UNAVAILABLE } from '../../lib/hands-won';

/**
 * THE COMMISSIONER'S FRONT PAGE.
 *
 * She opens this in the morning. The question it answers, in order, is: is anything on fire, is the
 * business working, and who is doing well.
 *
 * WANTS YOU TODAY COMES FIRST AND IS THE ONLY THING TINTED. Everything else on this page is a
 * number to know; that column is a list of things to do, and it is the only part that empties. On a
 * quiet morning it says so in a sentence and the rest of the page is just the business.
 *
 * THE SIX TILES NEVER TURN RUST ON A GUESS. A tile goes rust when it misses a target SHE set, and
 * she has set none yet — so today they are six numbers with their working shown underneath. Nothing
 * here invents a benchmark to fail against.
 *
 * AND A MEASURE NOBODY COULD TAKE IS A DASH, NOT A ZERO. "Return rate 0%" on a platform where no
 * league has finished is a lie that looks like a crisis. Every share on this page comes back null
 * until there is something to divide by, and renders as a dash with the reason beneath it.
 */

interface Measure {
  key: string;
  label: string;
  value: number | null;
  target: number | null;
  unit: 'count' | 'share';
  verdict: 'met' | 'missed' | 'unknown';
  basis: string | null;
}

interface LeagueInFlight {
  id: string;
  name: string;
  status: string;
  city: string | null;
  state: string | null;
  directorName: string | null;
  seats: number;
  cap: number;
  firstSessionAt: string | null;
  sessionsPlayed: number;
  sessionsTotal: number;
  sessionsScored: number;
  kitStatus: string | null;
}

interface DirectorRow {
  id: string;
  name: string | null;
  email: string;
  leaguesRun: number;
  leaguesActive: number;
  averageFill: number | null;
  scoreEntry: number | null;
  openTasks: number;
  overdueTasks: number;
}

interface TopPlayer {
  id: string;
  firstName: string;
  lastName: string;
  leagues: number;
  points: number;
  handsWon: number;
  handsWonVerified: boolean;
  sessionsWon: number;
}

interface ActionItem {
  key: string;
  urgency: 'today' | 'soon';
  text: string;
  href: string;
}

interface Pipeline {
  stages: { stage: string; count: number; markets: string[] }[];
  waiting: number;
}

interface Rating {
  /** Null below the threshold, and null before anybody answers. Never zero as a stand-in. */
  score: number | null;
  responses: number;
  minimum: number;
}

interface Dashboard {
  measures: Measure[];
  leaguesInFlight: LeagueInFlight[];
  directors: DirectorRow[];
  topPlayers: TopPlayer[];
  queue: ActionItem[];
  unreadNotes: number;
  pipeline: Pipeline;
  rating: Rating;
}

const DASH = '—';

function share(value: number | null): string {
  return value === null ? DASH : `${Math.round(value * 100)}%`;
}

function whole(value: number | null): string {
  return value === null ? DASH : String(value);
}

function shortDate(iso: string | null): string {
  if (!iso) return DASH;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const KIT_LABEL: Record<string, string> = {
  pending: 'Not started',
  files_ready: 'Files ready',
  printing: 'At the printer',
  assembled: 'Boxed',
  shipped: 'In transit',
  delivered: 'Arrived',
  returned: 'Returned',
};

export function HqDashboard() {
  const dashboard = useQuery({
    queryKey: ['hq', 'dashboard'],
    queryFn: () => apiFetch<Dashboard>('/api/hq/dashboard'),
  });

  if (dashboard.isPending) {
    return <p className="px-6 py-10 text-sm text-ink-3">Loading…</p>;
  }

  const data = dashboard.data;
  if (!data) return null;

  return (
    <div className="flex max-w-[96rem] flex-col gap-8 px-6 py-8">
      <header>
        <h1 className="font-display text-3xl leading-tight">The whole country</h1>
        <p className="mt-1 text-[14px] text-ink-2">
          What is running, how it is going, and what needs you.
        </p>
      </header>

      <div className="grid gap-8 xl:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="flex min-w-0 flex-col gap-8">
          <section>
            <dl className="grid gap-px overflow-hidden rounded-md border border-light bg-light sm:grid-cols-2 lg:grid-cols-3">
              {data.measures.map((m) => (
                <Kpi key={m.key} measure={m} />
              ))}
            </dl>
            {data.measures.every((m) => m.target === null) ? (
              <p className="mt-2 text-[12px] text-ink-3">
                No targets set, so nothing here can be failing yet.{' '}
                <Link to="/hq/settings" className={LINK_CLASS}>
                  Set what you want to hold the platform to
                </Link>
                .
              </p>
            ) : null}
          </section>

          <section className="min-w-0">
            <SectionHeading>Leagues in flight</SectionHeading>
            {data.leaguesInFlight.length === 0 ? (
              <Empty>Nothing is taking registrations or playing right now.</Empty>
            ) : (
              <div className="mt-3 overflow-x-auto" tabIndex={0} role="region" aria-label="Leagues in flight, scrollable">
              {/*
                FOCUSABLE, BECAUSE IT SCROLLS. A table wider than the panel scrolls sideways, and a
                mouse can drag it — a keyboard cannot reach it at all unless the region itself takes
                focus. These two hold read-only numbers, so unlike the directories table there is no
                link inside for a tab key to land on and carry the scroll.

                `role="region"` with a name is what stops it being an unlabelled stop on the tab
                order: a screen-reader user is told what she has arrived at rather than "group".
              */}
                <table className="w-full min-w-[46rem] border-collapse text-[14px]">
                  <thead>
                    <tr className="border-b border-stroke text-left">
                      <Th>League</Th>
                      <Th className="w-40">Director</Th>
                      <Th className="w-24">Session one</Th>
                      <Th className="w-28 pr-6 text-right">Roster</Th>
                      <Th className="w-28">Kit</Th>
                      <Th className="w-32 text-right">Sessions scored</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.leaguesInFlight.map((league) => {
                      /* One row, three health columns: is it full, has the box arrived, are the
                         scores in. A league is healthy when all three agree, and this is the
                         only place you can see that without opening it. */
                      const behind =
                        league.sessionsPlayed > league.sessionsScored ? league.sessionsPlayed - league.sessionsScored : 0;

                      return (
                        <tr key={league.id} className="border-b border-light align-top">
                          <td className="py-3 pr-3">
                            <div>{league.name}</div>
                            <div className="text-[12px] text-ink-3">
                              {league.city ? `${league.city}, ${league.state}` : 'Market not recorded'}
                            </div>
                          </td>
                          <td className="py-3 pr-3">{league.directorName ?? DASH}</td>
                          <td className="tabular py-3 pr-3">{shortDate(league.firstSessionAt)}</td>
                          <td className="tabular py-3 pr-6 text-right">
                            {league.seats} / {league.cap}
                          </td>
                          <td className="py-3 pr-3 text-[13px]">
                            {league.kitStatus ? KIT_LABEL[league.kitStatus] ?? league.kitStatus : DASH}
                          </td>
                          {/*
                            Out of the WHOLE league, not out of what has happened so far. "0 of 3"
                            on a league starting next month plainly means nothing yet; "0 of 0"
                            reads as a failure. Behind is a separate judgement, and it compares
                            what has been PLAYED against what has been scored.
                          */}
                          <td
                            className={[
                              'tabular py-3 text-right',
                              behind > 0 ? 'text-rust' : '',
                            ].join(' ')}
                          >
                            {league.sessionsScored} of {league.sessionsTotal}
                            {behind > 0 ? (
                              <div className="text-[12px]">
                                {behind} {behind === 1 ? 'session' : 'sessions'} missing
                              </div>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="min-w-0">
            <SectionHeading>Directors</SectionHeading>
            {data.directors.length === 0 ? (
              <Empty>No directors yet.</Empty>
            ) : (
              <div className="mt-3 overflow-x-auto" tabIndex={0} role="region" aria-label="Directors, scrollable">
              {/*
                FOCUSABLE, BECAUSE IT SCROLLS. A table wider than the panel scrolls sideways, and a
                mouse can drag it — a keyboard cannot reach it at all unless the region itself takes
                focus. These two hold read-only numbers, so unlike the directories table there is no
                link inside for a tab key to land on and carry the scroll.

                `role="region"` with a name is what stops it being an unlabelled stop on the tab
                order: a screen-reader user is told what she has arrived at rather than "group".
              */}
                <table className="w-full min-w-[42rem] border-collapse text-[14px]">
                  <thead>
                    <tr className="border-b border-stroke text-left">
                      <Th>Director</Th>
                      <Th className="w-24 text-right">Leagues</Th>
                      <Th className="w-28 text-right">Average fill</Th>
                      <Th className="w-32 text-right">Scores same day</Th>
                      <Th className="w-24 text-right">Overdue</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.directors.map((director) => (
                      <tr key={director.id} className="border-b border-light">
                        <td className="py-3 pr-3">
                          <div>{director.name ?? director.email}</div>
                          {director.leaguesActive > 0 ? (
                            <div className="text-[12px] text-ink-3">
                              {director.leaguesActive} running now
                            </div>
                          ) : null}
                        </td>
                        <td className="tabular py-3 pr-3 text-right">{director.leaguesRun}</td>
                        {/* A director who has never run a league has no fill rate. Zero would
                            rank her below somebody running a half-empty league, which is a
                            judgement nothing here has any basis for. */}
                        <td className="tabular py-3 pr-3 text-right">
                          {share(director.averageFill)}
                        </td>
                        <td className="tabular py-3 pr-3 text-right">
                          {share(director.scoreEntry)}
                        </td>
                        <td
                          className={[
                            'tabular py-3 text-right',
                            director.overdueTasks > 0 ? 'text-rust' : 'text-ink-3',
                          ].join(' ')}
                        >
                          {director.overdueTasks}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="min-w-0">
            <SectionHeading>Top players nationally</SectionHeading>
            {data.topPlayers.length === 0 ? (
              <Empty>Nobody has a score yet.</Empty>
            ) : (
              <ol className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {data.topPlayers.map((player, index) => (
                  <li
                    key={player.id}
                    className="rounded-md border border-light bg-paper px-5 py-4"
                  >
                    <div className="text-[11px] uppercase tracking-[0.14em] text-ink-3">
                      #{index + 1}
                    </div>
                    <div className="mt-1 text-[15px] font-medium">
                      {player.firstName} {player.lastName}
                    </div>
                    <div className="tabular mt-2 font-display text-[26px] leading-none">
                      {player.points}
                    </div>
                    <div className="mt-1.5 text-[12px] text-ink-2">
                      points · {player.handsWonVerified ? player.handsWon : '—'} hands · {player.sessionsWon}{' '}
                      {player.sessionsWon === 1 ? 'session won' : 'sessions won'}
                    </div>
                  </li>
                ))}
              </ol>
            )}
            {data.topPlayers.some((player) => !player.handsWonVerified) ? (
              <p className="mt-3 text-[12px] text-ink-3">{HANDS_WON_UNAVAILABLE}</p>
            ) : null}
          </section>
        </div>

        <aside className="flex min-w-0 flex-col gap-6">
          <section>
            <SectionHeading>Wants you today</SectionHeading>
            {data.queue.length === 0 ? (
              /* It empties. A list that only grows is a list nobody opens twice. */
              <p className="mt-3 rounded-md border border-dashed border-light px-5 py-8 text-center text-[13px] text-ink-3">
                Nothing needs you. Every session is scored, every pack is on time, and nobody is
                waiting on an answer.
              </p>
            ) : (
              <ul className="mt-3 flex flex-col gap-2">
                {data.queue.map((item) => (
                  <li key={item.key}>
                    <Link
                      to={item.href}
                      className={[
                        'block rounded-md border px-4 py-3 text-[13px] leading-relaxed',
                        item.urgency === 'today'
                          ? 'border-rust bg-tint-rust text-rust'
                          : 'border-light bg-paper hover:bg-sand',
                      ].join(' ')}
                    >
                      {item.text}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/*
            THE TWO PANELS THAT WERE BRACKETS. Both are real now — the funnel exists and the survey
            exists — and a bracket that outlives its cause is worse than one that never existed:
            it tells her there is nothing to see, on the screen she checks daily, about the two
            questions the whole operation turns on.

            Summaries, not boards. Each one is a click from the thing that does the work.
          */}
          {/*
            ⚠ THE PIPELINE PANEL IS GONE WHILE SCREENING IS OFF, and it is removed rather than
            emptied. Directors sign up and pay for themselves now, so there are no stages to move
            anybody through — a panel reading "nobody in the pipeline" would be true forever and
            would read, on the screen she checks daily, as a funnel that has stopped working.

            The panel, its data and the board behind it come back together with the flag.
          */}
          {DIRECTOR_SCREENING_IN_V1 ? (
          <section>
            <SectionHeading>Director pipeline</SectionHeading>
            {data.pipeline.stages.length === 0 ? (
              <p className="text-[13px] leading-relaxed text-ink-2">
                Nobody in the pipeline. The form is live at{' '}
                <span className="whitespace-nowrap">/directors/apply</span>.
              </p>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {data.pipeline.stages.map((row) => (
                  <li key={row.stage} className="flex items-baseline justify-between gap-3">
                    <Link
                      to="/hq/applications"
                      className="text-[13.5px] underline-offset-4 hover:underline"
                    >
                      {STAGES[row.stage as Stage].label}
                    </Link>
                    <span className="tabular text-[13.5px]">{row.count}</span>
                  </li>
                ))}
              </ul>
            )}
            {data.pipeline.waiting > 0 ? (
              /* The one number with a job attached: these people are waiting on HER. */
              <p className="mt-2 text-[12.5px] leading-relaxed text-signal-d">
                {data.pipeline.waiting === 1
                  ? '1 application nobody has looked at.'
                  : `${data.pipeline.waiting} applications nobody has looked at.`}
              </p>
            ) : null}
          </section>
          ) : null}

          <section>
            <SectionHeading>How players rate it</SectionHeading>
            {data.rating.score === null ? (
              /*
                NOT A BRACKET AND NOT A ZERO. An NPS of 0 is a real and quite bad result, so the
                absence of an answer has to read as an absence. It says which of the two reasons
                applies, because "nobody has been asked yet" and "not enough have answered to say"
                call for completely different things from her.
              */
              <p className="text-[13px] leading-relaxed text-ink-2">
                {data.rating.responses === 0
                  ? 'Nobody has answered a survey yet. One is created every time a league closes.'
                  : `${data.rating.responses} of the ${data.rating.minimum} answers needed before this means anything.`}
              </p>
            ) : (
              <>
                <p className="tabular font-display text-[32px] leading-none">
                  {data.rating.score > 0 ? `+${data.rating.score}` : data.rating.score}
                </p>
                <p className="mt-1.5 text-[12.5px] leading-relaxed text-ink-2">
                  Across {data.rating.responses}{' '}
                  {data.rating.responses === 1 ? 'answer' : 'answers'}, every league that has
                  closed.{' '}
                  <Link to="/hq/quality" className={LINK_CLASS}>
                    What they said
                  </Link>
                </p>
              </>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
}

function Kpi({ measure }: { measure: Measure }) {
  const missed = measure.verdict === 'missed';
  const rendered = measure.unit === 'share' ? share(measure.value) : whole(measure.value);

  /*
    dt BEFORE dd, and no <p> inside the <dl>.

    HTML allows one wrapper per pair — `dl > div > dt + dd` — and wants the term first. The working
    and the target were <p> elements directly under that wrapper, which made the whole thing read as
    a broken list to anything navigating by one. They belong inside the <dd> anyway: they qualify
    the number rather than standing beside it.

    The number reads above its label, so source order is dt then dd and `flex-col-reverse` turns it
    over. Markup order is what a screen reader follows; visual order is what an eye follows, and
    they may differ when the markup is the one obliged to be correct.
  */
  return (
    <div
      className={[
        'flex flex-col-reverse justify-end px-5 py-4',
        missed ? 'bg-tint-rust' : 'bg-paper',
      ].join(' ')}
    >
      <dt className={['mt-1.5 text-[13px]', missed ? 'text-rust' : 'text-ink-2'].join(' ')}>
        {measure.label}
      </dt>
      <dd
        className={['tabular font-display text-[30px] leading-none', missed ? 'text-rust' : ''].join(
          ' ',
        )}
      >
        {rendered}
        {/* The working, so a number never floats free of what it was taken from. */}
        {measure.basis ? (
          <span
            className={[
              'mt-1 block font-ui text-[12px] font-normal',
              missed ? 'text-rust' : 'text-ink-2',
            ].join(' ')}
          >
            {measure.basis}
          </span>
        ) : null}
        {measure.target !== null ? (
          <span className="mt-1 block font-ui text-[12px] font-normal text-ink-2">
            Target {measure.unit === 'share' ? share(measure.target) : measure.target}
          </span>
        ) : null}
      </dd>
    </div>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3">
      {children}
    </h2>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-3 rounded-md border border-dashed border-light px-5 py-8 text-center text-[13px] text-ink-3">
      {children}
    </p>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={[
        'pb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-3',
        className ?? '',
      ].join(' ')}
    >
      {children}
    </th>
  );
}
